import { patchHTML } from "../core/surface-host.js";
import { ensureAdminContentLayout } from "./layout.js";
import { escapeHTML, renderSkeleton, renderTruncatedID, timeAgo } from "../render/ui-helpers.js";
import { findPlaygroundRoute, playgroundCapability, playgroundPromptLabel, playgroundPromptPlaceholder, playgroundRequiresFileUpload, playgroundSupportsImageResponseFormat, playgroundSupportsStream } from "../inference-playground.js";

export function renderModelsRoute(layoutRoot, state, options = {}) {
  ensureAdminContentLayout(layoutRoot, "models", `
    <div class="entity-page">
      <div data-models-root></div>
    </div>
  `);
  const root = layoutRoot.querySelector("[data-models-root]");
  if (!state.apiKey) {
    patchHTML(root, options.renderLockedState?.() || "");
    return;
  }
  if (state.loading) {
    patchHTML(root, renderSkeleton(4));
    return;
  }
  if (!state.inferenceEnabled) {
    patchHTML(root, `
      <div class="entity-empty">
        <div class="entity-empty__title">Models backend is disabled</div>
        <div class="entity-empty__copy">Set <code>LYTEBULB_LLM_SECRET_KEY</code> to expose provider credentials, routes, traffic, and the playground.</div>
      </div>
    `);
    return;
  }
  patchHTML(root, renderModelsPage(state, options));
}

export function renderModelsPage(state, options = {}) {
  return `
    <div style="display:grid;gap:var(--space-xl);">
      ${renderCredentialDashboard(state, options)}
      <details class="disclosure" open>
        <summary>Playground</summary>
        ${renderPlaygroundSection(state, options)}
      </details>
      ${renderTrafficSection(state)}
    </div>
  `;
}

function renderCredentialDashboard(state, options = {}) {
  const draft = state.forms.inferenceCredential;
  const editing = state.editingInferenceCredentialID ? state.inferenceCredentials.find((item) => item.id === state.editingInferenceCredentialID) || null : null;
  const hasCredentials = state.inferenceCredentials.length > 0;
  const credentialRequired = !hasCredentials && !editing;
  const credFormVisible = credentialRequired || state.inferenceCredentialFormOpen || editing;
  const routeDraft = state.forms.inferenceRoute;
  const routeEditing = state.editingInferenceRouteID ? state.inferenceRoutes.find((item) => item.id === state.editingInferenceRouteID) || null : null;
  const canCreateRoute = hasCredentials || Boolean(routeEditing);
  const routeFormVisible = canCreateRoute && (state.inferenceRouteFormOpen || routeEditing);
  return `
    <section style="display:grid;gap:var(--space-sm);">
      <div style="display:flex;align-items:center;justify-content:space-between;">
        <div class="mini-label">Configuration</div>
        <div style="display:flex;gap:var(--space-xs);">
          ${credentialRequired ? "" : `<button class="button button--sm ${credFormVisible ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-inference-credential-form">${credFormVisible ? "Close" : "+ Credential"}</button>`}
          ${canCreateRoute ? `<button class="button button--sm ${routeFormVisible ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-inference-route-form">${routeFormVisible ? "Close" : "+ Route"}</button>` : ""}
        </div>
      </div>
      ${credFormVisible ? `
        <div class="entity-form-panel">
          ${editing ? `<div class="form-note">Editing <strong>${escapeHTML(editing.name)}</strong> ${renderTruncatedID(editing.id)}</div>` : ""}
          <form id="inference-credential-form" class="form-grid">
            <div class="form-grid form-grid--split">
              <div class="field">
                <label for="inference-credential-provider">Provider</label>
                <select id="inference-credential-provider" name="provider">
                  ${["openai", "openrouter", "groq", "xai", "compatible"].map((item) => `<option value="${item}" ${draft.provider === item ? "selected" : ""}>${item}</option>`).join("")}
                </select>
              </div>
              <div class="field">
                <label for="inference-credential-name">Name</label>
                <input id="inference-credential-name" name="name" placeholder="OpenAI default" value="${escapeHTML(draft.name)}">
              </div>
            </div>
            <div class="form-grid form-grid--split">
              <div class="field">
                <label for="inference-credential-ref">Credential ref</label>
                <input id="inference-credential-ref" name="credential_ref" placeholder="openai-default" value="${escapeHTML(draft.credential_ref)}">
                <div class="field-hint">Unique slug for API routing${!state.inferenceCredentialRefManuallyEdited && draft.credential_ref ? " (auto)" : ""}</div>
              </div>
              <div class="field">
                <label for="inference-credential-base-url">Base URL</label>
                <input id="inference-credential-base-url" name="base_url" placeholder="https://api.openai.com/v1" value="${escapeHTML(draft.base_url)}">
              </div>
            </div>
            <div class="form-grid form-grid--split">
              <div class="field">
                <label for="inference-credential-secret">Secret</label>
                <input id="inference-credential-secret" name="secret" type="password" placeholder="sk-..." value="">
                ${editing ? `<div class="field-hint">Secret set (v${escapeHTML(String(editing.secret_version || 0))}). Enter new to rotate.</div>` : ""}
              </div>
              <div class="field">
                <label for="inference-credential-scopes">Scopes CSV</label>
                <input id="inference-credential-scopes" name="scopes_csv" placeholder="responses,chat.completions,embeddings,audio.transcriptions,audio.translations,audio.speech,images.generate" value="${escapeHTML(draft.scopes_csv)}">
                <div class="field-hint">responses, chat.completions, embeddings, audio.transcriptions, audio.translations, audio.speech, images.generate</div>
              </div>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" name="enabled" ${draft.enabled ? "checked" : ""}>
              <span class="toggle-switch__track"></span>
              <span class="toggle-switch__label">Enabled</span>
            </label>
            ${state.formErrors.inferenceCredential ? `<div class="form-error">${escapeHTML(state.formErrors.inferenceCredential)}</div>` : ""}
            <div class="button-row">
              <button class="button button--primary" type="submit">${editing ? "Save credential" : "Create credential"}</button>
              ${editing ? `<button class="button button--ghost" type="button" data-action="cancel-inference-credential-edit">Cancel</button>` : ""}
            </div>
          </form>
        </div>
      ` : ""}
      ${routeFormVisible ? renderRouteForm(state, routeDraft, routeEditing) : ""}
      ${hasCredentials ? `
        <details class="disclosure">
          <summary>${state.inferenceCredentials.length} credential${state.inferenceCredentials.length !== 1 ? "s" : ""} · ${state.inferenceRoutes.length} route${state.inferenceRoutes.length !== 1 ? "s" : ""}</summary>
          <div class="entity-list">
            ${state.inferenceCredentials.map((item) => {
              const nestedRoutes = state.inferenceRoutes.filter((r) => r.credential_id === item.id);
              const credentialDeleteDisabled = nestedRoutes.length > 0;
              return `
                <article class="entity-row">
                  <div class="entity-row__main">
                    <div class="entity-row__body">
                      <div class="entity-row__title">${escapeHTML(item.name)}</div>
                      <div class="entity-row__meta">${escapeHTML(item.provider)} · ${escapeHTML(item.base_url || "no base_url")}</div>
                      <div class="entity-row__detail">ref: ${escapeHTML(item.credential_ref)} · secret v${escapeHTML(String(item.secret_version || 0))} · ${item.enabled ? "enabled" : "disabled"}</div>
                    </div>
                    <div class="entity-row__actions">
                      <button class="button button--sm button--ghost" type="button" data-action="toggle-inference-credential-enabled" data-inference-credential-id="${escapeHTML(item.id)}">${item.enabled ? "Disable" : "Enable"}</button>
                      <button class="button button--sm button--ghost" type="button" data-action="edit-inference-credential" data-inference-credential-id="${escapeHTML(item.id)}">Edit</button>
                      <button class="button button--sm button--danger" type="button" data-action="delete-inference-credential" data-inference-credential-id="${escapeHTML(item.id)}" ${credentialDeleteDisabled ? `disabled title="Delete attached routes first"` : ""}>Delete</button>
                    </div>
                  </div>
                  ${nestedRoutes.length ? `
                    <div class="entity-nested">
                      ${nestedRoutes.map((r) => {
                        const routeMeta = [
                          r.capability_family || "responses",
                          r.provider_client_id || "openai_compatible",
                          r.enabled ? "enabled" : "disabled",
                          r.is_default ? "default" : (r.match_mode || "no match"),
                          r.match_value || "",
                          r.default_model || ""
                        ].filter(Boolean).join(" · ");
                        return `
                        <div class="entity-row">
                          <div class="entity-row__main">
                            <div class="entity-row__body">
                              <div class="entity-row__title" style="font-size:var(--text-sm);">${escapeHTML(r.name)}</div>
                              <div class="entity-row__meta">${escapeHTML(routeMeta)}</div>
                            </div>
                            <div class="entity-row__actions">
                              <button class="button button--sm button--ghost" type="button" data-action="toggle-inference-route-enabled" data-inference-route-id="${escapeHTML(r.id)}">${r.enabled ? "Disable" : "Enable"}</button>
                              <button class="button button--sm button--ghost" type="button" data-action="edit-inference-route" data-inference-route-id="${escapeHTML(r.id)}">Edit</button>
                              <button class="button button--sm button--danger" type="button" data-action="delete-inference-route" data-inference-route-id="${escapeHTML(r.id)}">Delete</button>
                            </div>
                          </div>
                        </div>
                      `;}).join("")}
                    </div>
                  ` : ""}
                </article>
              `;
            }).join("")}
          </div>
        </details>
      ` : `
        <div class="entity-empty">
          <div class="entity-empty__title">No credentials configured</div>
          <div class="entity-empty__copy">Create a credential before adding routes.</div>
        </div>
      `}
    </section>
  `;
}

function renderRouteForm(state, draft, editing) {
  return `
    <div class="entity-form-panel" style="margin-top:var(--space-sm);">
      ${editing ? `<div class="form-note">Editing route <strong>${escapeHTML(editing.name)}</strong> ${renderTruncatedID(editing.id)}</div>` : `<div class="mini-label" style="margin-bottom:var(--space-sm);">New Route</div>`}
      <form id="inference-route-form" class="form-grid">
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="inference-route-name">Name</label>
            <input id="inference-route-name" name="name" placeholder="default-openai" value="${escapeHTML(draft.name)}">
          </div>
          <div class="field">
            <label for="inference-route-credential">Credential</label>
            <select id="inference-route-credential" name="credential_id">
              <option value="">Select credential</option>
              ${state.inferenceCredentials.map((item) => `<option value="${escapeHTML(item.id)}" ${draft.credential_id === item.id ? "selected" : ""}>${escapeHTML(item.name)}</option>`).join("")}
            </select>
          </div>
        </div>
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="inference-route-default-model">Default model</label>
            <input id="inference-route-default-model" name="default_model" placeholder="gpt-4.1-mini" value="${escapeHTML(draft.default_model)}">
          </div>
          <div class="field">
            <label for="inference-route-provider-client">Provider client</label>
            <input id="inference-route-provider-client" name="provider_client_id" value="${escapeHTML(draft.provider_client_id)}">
          </div>
        </div>
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="inference-route-capability-family">Capability family</label>
            <select id="inference-route-capability-family" name="capability_family">
              ${["responses", "embeddings", "audio_transcription", "audio_translation", "audio_speech", "image_generation"].map((item) => `<option value="${item}" ${draft.capability_family === item ? "selected" : ""}>${item}</option>`).join("")}
            </select>
          </div>
          <div style="display:flex;gap:var(--space-lg);align-items:center;padding-top:var(--space-lg);">
            <label class="toggle-switch">
              <input type="checkbox" name="enabled" ${draft.enabled ? "checked" : ""}>
              <span class="toggle-switch__track"></span>
              <span class="toggle-switch__label">Enabled</span>
            </label>
            <label class="toggle-switch">
              <input type="checkbox" name="is_default" ${draft.is_default ? "checked" : ""}>
              <span class="toggle-switch__track"></span>
              <span class="toggle-switch__label">Default route</span>
            </label>
          </div>
        </div>
        <details class="disclosure">
          <summary>Matching rules</summary>
          <div class="form-grid form-grid--split" style="padding-top:var(--space-sm);">
            <div class="field">
              <label for="inference-route-match-mode">Match mode</label>
              <select id="inference-route-match-mode" name="match_mode">
                <option value="" ${draft.match_mode === "" ? "selected" : ""}>default-only</option>
                <option value="exact_model" ${draft.match_mode === "exact_model" ? "selected" : ""}>exact_model</option>
                <option value="prefix_model" ${draft.match_mode === "prefix_model" ? "selected" : ""}>prefix_model</option>
              </select>
            </div>
            <div class="field">
              <label for="inference-route-match-value">Match value</label>
              <input id="inference-route-match-value" name="match_value" placeholder="gpt-4.1-mini" value="${escapeHTML(draft.match_value)}">
            </div>
          </div>
          <div class="field" style="padding-top:var(--space-sm);">
            <label for="inference-route-advertised-models">Advertised models CSV</label>
            <input id="inference-route-advertised-models" name="advertised_models_csv" placeholder="gpt-4.1-mini,gpt-4.1" value="${escapeHTML(draft.advertised_models_csv)}">
          </div>
        </details>
        <details class="disclosure">
          <summary>Advanced</summary>
          <div class="field" style="padding-top:var(--space-sm);">
            <label for="inference-route-base-url-override">Base URL override</label>
            <input id="inference-route-base-url-override" name="base_url_override" placeholder="Optional" value="${escapeHTML(draft.base_url_override)}">
          </div>
          <div class="form-grid form-grid--split" style="padding-top:var(--space-sm);">
            <div class="field">
              <label for="inference-route-timeout-ms">Timeout ms</label>
              <input id="inference-route-timeout-ms" name="timeout_ms" type="number" min="1" value="${escapeHTML(String(draft.timeout_ms))}">
            </div>
            <div class="field">
              <label for="inference-route-max-retries">Max retries</label>
              <input id="inference-route-max-retries" name="max_retries" type="number" min="0" max="5" value="${escapeHTML(String(draft.max_retries))}">
            </div>
          </div>
        </details>
        ${state.formErrors.inferenceRoute ? `<div class="form-error">${escapeHTML(state.formErrors.inferenceRoute)}</div>` : ""}
        <div class="button-row">
          <button class="button button--primary" type="submit">${editing ? "Save route" : "Create route"}</button>
          ${editing ? `<button class="button button--ghost" type="button" data-action="cancel-inference-route-edit">Cancel</button>` : ""}
        </div>
      </form>
    </div>
  `;
}

function renderTrafficSection(state) {
  const filter = state.inferenceTrafficFilter || "all";
  const filtered = state.inferenceTraffic.filter((item) => {
    if (filter === "success") return item.http_status >= 200 && item.http_status < 400;
    if (filter === "error") return item.http_status >= 400 || item.error_message;
    return true;
  });
  const limit = state.inferenceTrafficExpanded ? filtered.length : 10;
  const visible = filtered.slice(0, limit);
  return `
    <section>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--space-sm);">
        <div class="mini-label">Traffic</div>
        <div class="button-row">
          <button class="button button--sm ${filter === "all" ? "button--primary" : "button--ghost"}" type="button" data-action="inference-traffic-filter" data-filter="all">All</button>
          <button class="button button--sm ${filter === "success" ? "button--primary" : "button--ghost"}" type="button" data-action="inference-traffic-filter" data-filter="success">Success</button>
          <button class="button button--sm ${filter === "error" ? "button--primary" : "button--ghost"}" type="button" data-action="inference-traffic-filter" data-filter="error">Error</button>
        </div>
      </div>
      <div class="entity-list">
        ${visible.length ? visible.map((item) => {
          const isError = item.http_status >= 400 || item.error_message;
          const borderColor = isError ? "var(--danger)" : "var(--success)";
          const metaParts = [
            item.provider || "",
            item.provider_client_id || "",
            item.capability_family || "",
            item.purpose || "",
            timeAgo(item.created_at)
          ].filter(Boolean);
          const detailParts = [
            `HTTP ${String(item.http_status || 0)}`,
            `${String(item.latency_ms || 0)} ms`,
            item.transport_kind || (item.streamed ? "sse" : "json"),
            item.route_id ? `route ${item.route_id}` : "",
            item.id ? `request ${item.id}` : ""
          ].filter(Boolean);
          return `
          <article class="entity-row" style="border-left:3px solid ${borderColor};">
            <div class="entity-row__main">
              <div class="entity-row__body">
                <div class="entity-row__title">${escapeHTML(item.model || "unknown model")} · ${escapeHTML(item.status)}</div>
                <div class="entity-row__meta">${escapeHTML(metaParts.join(" · "))}</div>
                <div class="entity-row__detail">${escapeHTML(detailParts.slice(0, 3).join(" · "))}${detailParts.length > 3 ? ` · ${escapeHTML(detailParts[3])}` : ""}${item.id ? ` · ${renderTruncatedID(item.id)}` : ""}</div>
                ${item.error_message ? `<details style="margin-top:var(--space-xs);"><summary style="font-size:var(--text-xs);color:var(--danger);cursor:pointer;">Error details</summary><div class="form-note form-note--warn">${escapeHTML(item.error_message)}</div></details>` : ""}
              </div>
            </div>
          </article>`;
        }).join("") : `
          <div class="entity-empty">
            <div class="entity-empty__title">${filter !== "all" ? `No ${filter} traffic` : "No traffic yet"}</div>
            <div class="entity-empty__copy">${filter !== "all" ? "Try a different filter." : "Direct inference requests and playground runs will appear here."}</div>
          </div>
        `}
      </div>
      ${!state.inferenceTrafficExpanded && filtered.length > 10 ? `
        <div style="text-align:center;margin-top:var(--space-sm);">
          <button class="button button--sm button--ghost" type="button" data-action="inference-traffic-expand">View all ${filtered.length} entries</button>
        </div>
      ` : ""}
    </section>
  `;
}

function extractResponseText(response) {
  if (!response) return "";
  if (typeof response === "string") return response;
  if (response.choices && Array.isArray(response.choices) && response.choices.length > 0) {
    const choice = response.choices[0];
    if (choice.message && typeof choice.message.content === "string") return choice.message.content;
  }
  if (response.output && Array.isArray(response.output)) {
    const textParts = response.output
      .filter((item) => item.type === "message" && Array.isArray(item.content))
      .flatMap((item) => item.content)
      .filter((part) => part.type === "output_text" && typeof part.text === "string")
      .map((part) => part.text);
    if (textParts.length > 0) return textParts.join("\n\n");
  }
  return "";
}

function renderPlaygroundSection(state, options = {}) {
  const draft = state.forms.inferencePlayground;
  const response = state.inferencePlaygroundResponse;
  const selectedRoute = findPlaygroundRoute(state.inferenceRoutes, draft.route_id);
  const capability = playgroundCapability(selectedRoute);
  const supportsStream = playgroundSupportsStream(selectedRoute);
  return `
    <div class="entity-form-panel">
      <form id="inference-playground-form" class="form-grid">
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="inference-playground-route">Route</label>
            <select id="inference-playground-route" name="route_id">
              <option value="">Select route</option>
              ${state.inferenceRoutes.filter((item) => item.enabled).map((item) => `<option value="${escapeHTML(item.id)}" ${draft.route_id === item.id ? "selected" : ""}>${escapeHTML(item.name)} · ${escapeHTML(item.capability_family || "responses")}</option>`).join("")}
            </select>
            ${selectedRoute ? `<div class="field-hint">Capability: ${escapeHTML(capability)}</div>` : ""}
          </div>
          <div class="field">
            <label for="inference-playground-model">Model</label>
            <input id="inference-playground-model" name="model" placeholder="Optional, defaults from route" value="${escapeHTML(draft.model)}">
          </div>
        </div>
        ${capability === "responses" ? `
          <div class="field">
            <label for="inference-playground-system">System prompt</label>
            <textarea id="inference-playground-system" name="system_prompt" rows="2" placeholder="Optional system instructions">${escapeHTML(draft.system_prompt)}</textarea>
          </div>
        ` : ""}
        ${playgroundRequiresFileUpload(selectedRoute) ? `
          <div class="field">
            <label for="inference-playground-file">File</label>
            <input id="inference-playground-file" name="file" type="file" accept="audio/*">
            <div class="field-hint">Audio upload required for ${escapeHTML(capability)}.</div>
          </div>
        ` : ""}
        <div class="field">
          <label for="inference-playground-prompt">${escapeHTML(playgroundPromptLabel(selectedRoute))}</label>
          <textarea id="inference-playground-prompt" name="prompt" rows="4" placeholder="${escapeHTML(playgroundPromptPlaceholder(selectedRoute))}">${escapeHTML(draft.prompt)}</textarea>
        </div>
        ${renderPlaygroundParameters(selectedRoute, draft, capability, supportsStream)}
        ${state.formErrors.inferencePlayground ? `<div class="form-error">${escapeHTML(state.formErrors.inferencePlayground)}</div>` : ""}
        <div class="button-row">
          <button class="button button--primary" type="submit">${state.inferencePlaygroundLoading ? "Running..." : "Run playground request"}</button>
          ${response ? `<button class="button button--ghost" type="button" data-action="clear-inference-playground">Clear response</button>` : ""}
        </div>
      </form>
    </div>
    ${state.inferencePlaygroundLoading ? `
      <div class="entity-form-panel" style="border-left:3px solid var(--amber);display:flex;align-items:center;gap:var(--space-sm);">
        <span class="thinking-dot"></span>
        <span style="color:var(--ink-soft);font-size:var(--text-sm);">Waiting for response...</span>
      </div>
    ` : ""}
    ${!state.inferencePlaygroundLoading && response ? renderPlaygroundResponse(state, response) : ""}
  `;
}

function renderPlaygroundParameters(route, draft, capability, supportsStream) {
  return `
    <div style="display:flex;align-items:center;gap:var(--space-lg);">
      <details class="disclosure" style="flex:1;">
        <summary>Parameters</summary>
        <div class="form-grid form-grid--split" style="padding-top:var(--space-sm);">
          ${renderCapabilitySpecificParameterFields(route, draft, capability)}
        </div>
      </details>
      ${supportsStream ? `
        <label class="toggle-switch">
          <input type="checkbox" name="stream" ${draft.stream ? "checked" : ""}>
          <span class="toggle-switch__track"></span>
          <span class="toggle-switch__label">Stream</span>
        </label>
      ` : ""}
    </div>
  `;
}

function renderCapabilitySpecificParameterFields(route, draft, capability) {
  switch (capability) {
  case "embeddings":
    return `
      <div class="field">
        <label for="inference-playground-dimensions">Dimensions</label>
        <input id="inference-playground-dimensions" name="dimensions" type="number" min="1" placeholder="Optional" value="${escapeHTML(draft.dimensions)}">
      </div>
      <div class="field">
        <label for="inference-playground-encoding-format">Encoding format</label>
        <input id="inference-playground-encoding-format" name="encoding_format" placeholder="float" value="${escapeHTML(draft.encoding_format)}">
      </div>
    `;
  case "audio_transcription":
    return `
      <div class="field">
        <label for="inference-playground-language">Language</label>
        <input id="inference-playground-language" name="language" placeholder="Optional language hint" value="${escapeHTML(draft.language)}">
      </div>
      <div class="field">
        <label for="inference-playground-response-format">Response format</label>
        <input id="inference-playground-response-format" name="response_format" placeholder="json" value="${escapeHTML(draft.response_format)}">
      </div>
      <div class="field">
        <label for="inference-playground-temperature">Temperature</label>
        <input id="inference-playground-temperature" name="temperature" type="number" step="0.1" min="0" max="2" placeholder="0.0" value="${escapeHTML(draft.temperature)}">
      </div>
      <div class="field"></div>
    `;
  case "audio_translation":
    return `
      <div class="field">
        <label for="inference-playground-response-format">Response format</label>
        <input id="inference-playground-response-format" name="response_format" placeholder="json" value="${escapeHTML(draft.response_format)}">
      </div>
      <div class="field">
        <label for="inference-playground-temperature">Temperature</label>
        <input id="inference-playground-temperature" name="temperature" type="number" step="0.1" min="0" max="2" placeholder="0.0" value="${escapeHTML(draft.temperature)}">
      </div>
      <div class="field"></div>
      <div class="field"></div>
    `;
  case "audio_speech":
    return `
      <div class="field">
        <label for="inference-playground-voice">Voice</label>
        <input id="inference-playground-voice" name="voice" placeholder="alloy" value="${escapeHTML(draft.voice)}">
      </div>
      <div class="field">
        <label for="inference-playground-response-format">Response format</label>
        <input id="inference-playground-response-format" name="response_format" placeholder="mp3" value="${escapeHTML(draft.response_format)}">
      </div>
      <div class="field">
        <label for="inference-playground-speed">Speed</label>
        <input id="inference-playground-speed" name="speed" type="number" step="0.05" min="0.25" max="4" placeholder="1.0" value="${escapeHTML(draft.speed)}">
      </div>
      <div class="field"></div>
    `;
  case "image_generation":
    {
      const supportsResponseFormat = playgroundSupportsImageResponseFormat(route, draft);
    return `
      <div class="field">
        <label for="inference-playground-size">Size</label>
        <input id="inference-playground-size" name="size" placeholder="1024x1024" value="${escapeHTML(draft.size)}">
      </div>
      <div class="field">
        <label for="inference-playground-quality">Quality</label>
        <input id="inference-playground-quality" name="quality" placeholder="standard" value="${escapeHTML(draft.quality)}">
      </div>
      ${supportsResponseFormat ? `
        <div class="field">
          <label for="inference-playground-response-format">Response format</label>
          <input id="inference-playground-response-format" name="response_format" placeholder="url or b64_json" value="${escapeHTML(draft.response_format)}">
        </div>
      ` : `
        <div class="field">
          <label>Response format</label>
          <div class="field-hint">The selected GPT Image model does not accept <code>response_format</code>. Lytebulb will omit it.</div>
        </div>
      `}
      <div class="field">
        <label for="inference-playground-n">Count</label>
        <input id="inference-playground-n" name="n" type="number" min="1" placeholder="1" value="${escapeHTML(draft.n)}">
      </div>
    `;
    }
  case "responses":
  default:
    return `
      <div class="field">
        <label for="inference-playground-temperature">Temperature</label>
        <input id="inference-playground-temperature" name="temperature" type="number" step="0.1" min="0" max="2" placeholder="0.7" value="${escapeHTML(draft.temperature)}">
      </div>
      <div class="field">
        <label for="inference-playground-max-tokens">Max tokens</label>
        <input id="inference-playground-max-tokens" name="max_tokens" type="number" min="1" placeholder="4096" value="${escapeHTML(draft.max_tokens)}">
      </div>
      <div class="field">
        <label for="inference-playground-top-p">Top P</label>
        <input id="inference-playground-top-p" name="top_p" type="number" step="0.05" min="0" max="1" placeholder="1.0" value="${escapeHTML(draft.top_p)}">
      </div>
      <div class="field"></div>
    `;
  }
}

function renderPlaygroundResponse(state, response) {
  const showRaw = state.inferencePlaygroundShowRawJSON;
  const responseText = extractResponseText(response.response);
  const capability = String(response.capability_family || "responses");
  const metaParts = [
    capability,
    response.request?.status || "",
    response.request?.latency_ms ? `${response.request.latency_ms} ms` : "",
    response.request?.model || ""
  ].filter(Boolean);
  return `
    <div class="entity-form-panel" style="border-left:3px solid var(--teal);background:var(--teal-soft);">
      <div style="display:flex;align-items:center;justify-content:space-between;">
        <div class="mini-label">Response</div>
        <button class="button button--sm button--ghost" type="button" data-action="inference-playground-toggle-raw">${showRaw ? "View text" : "View raw"}</button>
      </div>
      ${metaParts.length ? `<div class="entity-row__meta" style="margin-bottom:var(--space-sm);">${escapeHTML(metaParts.join(" · "))}</div>` : ""}
      ${showRaw
        ? `<pre style="margin:0;white-space:pre-wrap;overflow:auto;">${escapeHTML(JSON.stringify(response.response, null, 2))}</pre>`
        : renderCapabilityPlaygroundResponse(capability, response, responseText)
      }
    </div>
  `;
}

function renderCapabilityPlaygroundResponse(capability, response, responseText) {
  if (capability === "image_generation") {
    return renderImageGenerationPlaygroundResponse(response);
  }
  if (response.download_url) {
    return renderBinaryPlaygroundResponse(response);
  }
  if (capability === "embeddings") {
    const count = Array.isArray(response.response?.data) ? response.response.data.length : 0;
    return `<div style="white-space:pre-wrap;">${count > 0 ? `${count} embedding vector${count === 1 ? "" : "s"} returned.` : `<span style="color:var(--ink-dim);">No embedding data returned. Switch to raw view.</span>`}</div>`;
  }
  return `<div style="white-space:pre-wrap;">${responseText ? escapeHTML(responseText) : `<span style="color:var(--ink-dim);">No text content extracted. Switch to raw view.</span>`}</div>`;
}

function renderImageGenerationPlaygroundResponse(response) {
  const items = Array.isArray(response.response?.data) ? response.response.data : [];
  if (items.length === 0) {
    return `<div style="color:var(--ink-dim);">No image data returned. Switch to raw view.</div>`;
  }
  return `
    <div style="display:grid;gap:var(--space-md);">
      ${items.map((item, index) => {
        const imageSrc = String(item.url || "").trim() || (String(item.b64_json || "").trim() ? `data:image/png;base64,${item.b64_json}` : "");
        return `
          <div style="display:grid;gap:var(--space-xs);">
            ${imageSrc ? `<img src="${escapeHTML(imageSrc)}" alt="Generated image ${index + 1}" style="max-width:100%;border-radius:var(--radius-md);border:1px solid var(--line);background:var(--surface-raised);">` : ""}
            <div style="display:flex;gap:var(--space-sm);flex-wrap:wrap;">
              ${String(item.url || "").trim() ? `<a class="button button--sm button--ghost" href="${escapeHTML(item.url)}" target="_blank" rel="noreferrer">Open image</a>` : ""}
              ${imageSrc ? `<a class="button button--sm button--ghost" href="${escapeHTML(imageSrc)}" download="generated-image-${index + 1}.png">Download</a>` : ""}
            </div>
            ${item.revised_prompt ? `<div class="entity-row__meta">Revised prompt: ${escapeHTML(item.revised_prompt)}</div>` : ""}
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function renderBinaryPlaygroundResponse(response) {
  const isAudio = String(response.content_type || "").toLowerCase().startsWith("audio/");
  return `
    <div style="display:grid;gap:var(--space-sm);">
      ${isAudio ? `<audio controls src="${escapeHTML(response.download_url)}" style="width:100%;"></audio>` : ""}
      <div style="display:flex;gap:var(--space-sm);flex-wrap:wrap;">
        <a class="button button--sm button--ghost" href="${escapeHTML(response.download_url)}" download="${escapeHTML(response.filename || "download.bin")}">Download file</a>
      </div>
      ${response.content_type ? `<div class="entity-row__meta">Content type: ${escapeHTML(response.content_type)}</div>` : ""}
    </div>
  `;
}
